import type { Dispatch } from "@reduxjs/toolkit";
import { setDefaultApiKey } from "redux/slices/apiKeySlice";
import { setApiKey } from "redux/slices/userDataSlice";
import { deleteCookie, getCookie, setCookie } from "utils/cookieManagement";
import { decodeBase64 } from "utils/base64/decodeData";

/**
 * The ONE place the signed-in user's api key is written.
 *
 * The key lives in four places that must never disagree: redux `apiKey.apiKey`
 * (what data calls send), redux `userData.apiKey`, `localStorage.api_key`
 * (what survives a reload) and the legacy `ses_userdata` cookie. Sign-in
 * flows and the 401 recovery all go through `storeUserApiKey` so a rotation
 * updates every copy in one call.
 *
 * localStorage is AUTHORITATIVE at bootstrap (`resolveBootstrapApiKey`): a
 * cookie can exist in several path/domain variants the page cannot tell apart,
 * so a stale copy must never override the key the store last wrote.
 */

export const API_KEY_STORAGE_KEY = "api_key";
const SESSION_COOKIE = "ses_userdata";

/** A key that belongs to a user: present, not a stringified null, not the site's anonymous key. */
export const isUserApiKey = (key: string | null | undefined): key is string =>
  typeof key === "string" &&
  key.trim() !== "" &&
  key !== "undefined" &&
  key !== "null" &&
  key !== process.env.REACT_APP_DEFAULT_API_KEY;

/** The user key persisted in localStorage, or null. */
export const getStoredApiKey = (): string | null => {
  const key = localStorage.getItem(API_KEY_STORAGE_KEY);
  return isUserApiKey(key) ? key : null;
};

/**
 * Decode the legacy `ses_userdata` cookie (base64 JSON). Returns null when the
 * cookie is absent or not decodable, instead of throwing inside a render
 * effect.
 */
export const readSessionUserData = (): any | null => {
  const raw = getCookie(SESSION_COOKIE);
  if (!raw) return null;
  try {
    return decodeBase64(raw);
  } catch {
    return null;
  }
};

/** The api key carried by the `ses_userdata` cookie, if any. */
export const getSessionCookieApiKey = (
  sessionData: any = readSessionUserData()
): string | undefined => sessionData?.data?.api_token || sessionData?.api_token;

/**
 * The api key to start the app with: the user key in localStorage when there
 * is one, else the key carried by the `ses_userdata` cookie (legacy sessions
 * that predate the key store).
 */
export const resolveBootstrapApiKey = (
  sessionData: any = readSessionUserData()
): string | undefined => getStoredApiKey() ?? getSessionCookieApiKey(sessionData);

// Rewrite the api key inside `ses_userdata` when that cookie exists; never
// create it. Both historical shapes (`data.api_token`, top-level `api_token`)
// are kept in step. Known variants (default path and `/`) are expired first
// so the rewrite replaces the cookie instead of adding a twin next to it.
const updateSessionCookieApiKey = (key: string): void => {
  const sessionData = readSessionUserData();
  if (!sessionData || typeof sessionData !== "object") return;
  if (sessionData.data && typeof sessionData.data === "object") {
    sessionData.data.api_token = key;
  }
  if ("api_token" in sessionData || !sessionData.data) {
    sessionData.api_token = key;
  }
  let encoded: string;
  try {
    encoded = btoa(JSON.stringify(sessionData));
  } catch {
    // Non-Latin-1 payload: leave the cookie; localStorage + redux are updated
    // and localStorage wins over the stale cookie at bootstrap.
    return;
  }
  deleteCookie(SESSION_COOKIE);
  setCookie(SESSION_COOKIE, encoded, false, undefined, "/");
};

/**
 * Store the signed-in user's api key everywhere it is read from.
 * Throws on a non-user key (empty or the anonymous default) so a sign-in flow
 * can never persist the site key as the user's.
 */
export const storeUserApiKey = (dispatch: Dispatch, key: string): void => {
  if (!isUserApiKey(key)) {
    throw new Error("Refusing to store a non-user api key");
  }
  localStorage.setItem(API_KEY_STORAGE_KEY, key);
  updateSessionCookieApiKey(key);
  adoptUserApiKey(dispatch, key);
};

/**
 * Point this tab's redux copies at a key another tab already persisted
 * (localStorage and the cookie are shared, so only the in-memory store lags).
 */
export const adoptUserApiKey = (dispatch: Dispatch, key: string): void => {
  dispatch(setDefaultApiKey(key));
  dispatch(setApiKey(key));
};
